import { ref, computed } from 'vue'
import { pwaConfig } from './pwaTools'
import { tryReloadForServerVersionMismatch } from './versionCheck'
import {
  reportSwActivated,
  reportSwControllerChange,
  reportSwInstalled,
  reportSwUpdateFound,
} from './swLifecycleTelemetry'
import { shouldAttemptSwUpdate, VISIBLE_TAB_DEPLOY_SYNC_INTERVAL_MS } from './swUpdateSchedule'

const SW_SCRIPT_URL = '/sw.js'

const swRegistered = ref(false)
const swRegistration = ref<ServiceWorkerRegistration | null>(null)
const swInstalling = ref<ServiceWorker | null>(null)
const swWaiting = ref<ServiceWorker | null>(null)
const swActive = ref<ServiceWorker | null>(null)
const swController = ref<ServiceWorker | null>(
  typeof navigator !== 'undefined' && 'serviceWorker' in navigator
    ? navigator.serviceWorker.controller
    : null,
)

let lastVersionPollMs = 0
let lastSwUpdateAttemptMs = 0
let visibilityListenerAttached = false
let versionPollIntervalId: ReturnType<typeof setInterval> | undefined
let initialUpdateAttempted = false

const registrationsWithListeners = new WeakSet<ServiceWorkerRegistration>()
const monitoredInstallingWorkers = new WeakSet<ServiceWorker>()

const updateServiceWorkerStates = (reg: ServiceWorkerRegistration | null) => {
  if (!reg) {
    swInstalling.value = null
    swWaiting.value = null
    swActive.value = null
    return
  }

  swInstalling.value = reg.installing
  swWaiting.value = reg.waiting
  swActive.value = reg.active
  swController.value = navigator.serviceWorker.controller

  if (swInstalling.value) {
    toastDebug('SW: installing', 20)
  }
  if (swWaiting.value) {
    toastDebug('SW: waiting', 20)
  }
}

const attemptRegistrationUpdate = (reg: ServiceWorkerRegistration, now = Date.now()): void => {
  lastSwUpdateAttemptMs = now
  reg.update().catch((error) => {
    console.error('SW: update error', error)
    // Ignore update errors; registration will retry later.
  })
}

const maybeAttemptRegistrationUpdate = (reg: ServiceWorkerRegistration): void => {
  const now = Date.now()
  if (!shouldAttemptSwUpdate(now, lastSwUpdateAttemptMs)) {
    return
  }
  attemptRegistrationUpdate(reg, now)
}

/**
 * Visible-tab deploy sync: poll `/version.json` and act when its `version` differs from
 * `__APP_VERSION__` (build-time git SHA).
 *
 * No periodic `reg.update()` on matching polls (avoids SW install churn). On a mismatch,
 * `tryReloadForServerVersionMismatch` calls `reg.update()` itself when a SW controls the tab, and
 * leaves the reload to `controllerchange` so it is served by the new precache. It reloads directly
 * only without a controller or when no new worker installs.
 */
export const runVisibleTabDeploySync = async (options?: {
  now?: number
  enforceMinInterval?: boolean
}): Promise<void> => {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
    return
  }

  const now = options?.now ?? Date.now()
  if (options?.enforceMinInterval !== false && !shouldAttemptSwUpdate(now, lastVersionPollMs)) {
    return
  }

  lastVersionPollMs = now
  await tryReloadForServerVersionMismatch('version_poll')
}

const attachVisibilityUpdateListener = () => {
  if (visibilityListenerAttached || typeof document === 'undefined') {
    return
  }

  visibilityListenerAttached = true
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      return
    }

    const reg = swRegistration.value
    if (reg) {
      updateServiceWorkerStates(reg)
      maybeAttemptRegistrationUpdate(reg)
    }

    void runVisibleTabDeploySync({ enforceMinInterval: true })
  })
}

const startVisibleTabVersionPollInterval = () => {
  if (versionPollIntervalId !== undefined || typeof document === 'undefined') {
    return
  }

  versionPollIntervalId = setInterval(() => {
    void runVisibleTabDeploySync({ enforceMinInterval: false })
  }, VISIBLE_TAB_DEPLOY_SYNC_INTERVAL_MS)
}

const monitorWorkerState = (worker: ServiceWorker, reg: ServiceWorkerRegistration): void => {
  if (monitoredInstallingWorkers.has(worker)) {
    return
  }
  monitoredInstallingWorkers.add(worker)

  const stateChangeHandler = () => {
    updateServiceWorkerStates(reg)

    if (worker.state === 'installed') {
      reportSwInstalled()
      updateServiceWorkerStates(reg)
    } else if (worker.state === 'activated') {
      reportSwActivated()
      updateServiceWorkerStates(reg)
      worker.removeEventListener('statechange', stateChangeHandler)
    } else if (worker.state === 'redundant') {
      worker.removeEventListener('statechange', stateChangeHandler)
    }
  }

  worker.addEventListener('statechange', stateChangeHandler)
}

const attachRegistrationListeners = (reg: ServiceWorkerRegistration) => {
  if (registrationsWithListeners.has(reg)) {
    return
  }
  registrationsWithListeners.add(reg)

  reg.addEventListener('updatefound', () => {
    const newWorker = reg.installing
    if (!newWorker) return

    reportSwUpdateFound(Boolean(reg.active))
    swInstalling.value = newWorker
    monitorWorkerState(newWorker, reg)
  })

  if (reg.installing) {
    swInstalling.value = reg.installing
    monitorWorkerState(reg.installing, reg)
  }
  if (reg.waiting) {
    swWaiting.value = reg.waiting
    reg.waiting.addEventListener('statechange', () => {
      updateServiceWorkerStates(reg)
    })
  }
  if (reg.active) {
    swActive.value = reg.active
    reg.active.addEventListener('statechange', () => {
      updateServiceWorkerStates(reg)
    })
  }
}

const handleRegistrationSuccess = (reg: ServiceWorkerRegistration): void => {
  swRegistration.value = reg
  updateServiceWorkerStates(reg)
  attachRegistrationListeners(reg)
  swRegistered.value = true

  if (!initialUpdateAttempted) {
    initialUpdateAttempted = true
    attemptRegistrationUpdate(reg)
  }
}

/**
 * Deploy: auto-reload when a new service worker takes control (optional).
 *
 * Our SW uses skipWaiting + clients.claim (see sw.ts), so after a deploy the new worker
 * can control this tab while the page still runs an older in-memory JS bundle. That
 * mismatch causes subtle bugs until the user refreshes.
 *
 * On controllerchange we compare /version.json `version` to __APP_VERSION__ and reload once per
 * server build (session limits live in versionCheck.ts, shared with chunk recovery). This is the
 * reload that loads the new bundle; do not call `reg.update()` from this handler.
 *
 * While the tab is visible we poll `/version.json` every 90s (`version_poll`). On mismatch with a
 * controlling SW, the poll calls `reg.update()` and waits for this controllerchange instead of
 * reloading under the old precache. `reg.update()` also runs on register and when the tab becomes
 * visible (debounced).
 *
 * We register the SW ourselves (injectRegister: false) so vite-plugin-pwa does not attach
 * its autoUpdate `activated` → location.reload() handler.
 */
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const hadPriorController = Boolean(swController.value)
    swController.value = navigator.serviceWorker.controller
    reportSwControllerChange(hadPriorController)
    void tryReloadForServerVersionMismatch('sw_controllerchange')
  })
}

export const trackServiceWorkerRegistrationAndUpdate = () => {
  if (pwaConfig.isDisabled) {
    return
  }

  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return
  }

  attachVisibilityUpdateListener()
  startVisibleTabVersionPollInterval()

  void navigator.serviceWorker
    .register(SW_SCRIPT_URL)
    .then((reg) => {
      handleRegistrationSuccess(reg)
    })
    .catch((error) => {
      console.error('SW: registration error', error)
      if (swRegistration.value) {
        return
      }

      void navigator.serviceWorker
        .getRegistration()
        .then((reg) => {
          if (reg) {
            handleRegistrationSuccess(reg)
          } else {
            updateServiceWorkerStates(null)
          }
        })
        .catch((err) => {
          console.error('SW: getRegistration error', err)
          updateServiceWorkerStates(null)
        })
    })
}

export const useServiceWorkerRegistered = () => {
  return {
    isServiceWorkerRegistered: swRegistered,
    swRegistration: computed(() => swRegistration.value),
    swInstalling: computed(() => swInstalling.value),
    swWaiting: computed(() => swWaiting.value),
    swActive: computed(() => swActive.value),
    swController: computed(() => swController.value),
    swState: computed(() => {
      if (swInstalling.value) return 'installing'
      if (swWaiting.value) return 'waiting'
      if (swActive.value) return 'active'
      return 'none'
    }),
  }
}
